import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, ChevronLeft, ChevronRight, Download, Eye, FileSpreadsheet, FileText, History, RotateCcw, Search, X, XCircle } from "lucide-react";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { Select } from "#/components/common/Select";
import { Modal } from "#/components/common/Modal";
import { useToast } from "#/components/common/ToastProvider";
import { exportToExcel, exportToPdf } from "#/lib/exportUtils";
import { auditApi } from "../api";
import { useAuditEvents } from "../hooks";
import { AUDIT_ACTIONS, AUDIT_MODULES } from "../types";
import type { AuditActorOption, AuditEvent, AuditSearchFilters } from "../types";

/** Debounces a fast-changing value — used so free-typed filters (entity type, actor search,
 * date fields) don't fire a new search on every keystroke. */
function useDebouncedValue<T>(value: T, delayMs = 400): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

const PAGE_SIZE = 20;

/** Converts a native `<input type="datetime-local">` value (always the BROWSER'S local
 * wall-clock time, with no time-zone marker at all) into a proper UTC instant string the
 * backend's `Instant` filters can parse. `new Date(value)` treats the zone-less string as
 * local time (correct — that's what the picker showed), and `.toISOString()` converts that
 * to UTC with a trailing "Z". Without this step the naive string was sent to the server
 * as-is and misread there as the SERVER's own local time instead of the user's.
 */
function localDateTimeInputToIso(value: string): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString();
}

/** Plain-language names for the technical request paths that show up in some events'
 * details, so the "What happened" panel never shows a raw API route to a non-technical reader. */
const FRIENDLY_ACTION_NAMES: Record<string, string> = {
  "/api/auth/login": "signing in",
  "/api/auth/mfa/verify": "the two-factor verification code",
  "/api/auth/mfa/challenge/email": "sending a two-factor code by email",
  "/api/auth/password-reset/request": "the password reset request",
  "/api/auth/password-reset/complete": "resetting the password",
  "/api/auth/account-setup/complete": "setting up the account",
};

/** Audit events store a compact `outcome=...; method=...; path=...; <message>` string in
 * `details` for easy logging/debugging. Turning that back into plain English for the "What
 * happened" panel is what this does: a clear succeeded/failed badge plus a one-line
 * description, instead of showing the raw technical string to everyday users. */
function describeAuditDetails(details: string) {
  const outcomeMatch = details.match(/outcome=(\w+)/i);
  const pathMatch = details.match(/path=([^;]+)/i);
  const outcome = outcomeMatch?.[1]?.toUpperCase() ?? null;
  const succeeded = outcome === "SUCCESS";
  const failed = outcome === "FAILED" || outcome === "FAILURE" || outcome === "ERROR";

  const message = details
    .replace(/outcome=\w+;?\s*/i, "")
    .replace(/method=\w+;?\s*/i, "")
    .replace(/path=[^;]+;?\s*/i, "")
    .trim();

  const friendlyAction = pathMatch ? FRIENDLY_ACTION_NAMES[pathMatch[1].trim()] : null;

  return {
    succeeded,
    failed,
    statusLabel: succeeded ? "Succeeded" : failed ? "Failed" : outcome,
    message: message || (friendlyAction ? `Related to ${friendlyAction}.` : null),
    raw: details,
  };
}

const MODULE_BADGE: Record<string, string> = {
  HR: "bg-slate-100 text-slate-700",
  LEAVE: "bg-emerald-50 text-emerald-700",
  PAYROLL: "bg-lime-50 text-lime-700",
  ATTENDANCE: "bg-cyan-50 text-cyan-700",
  NOTIFICATION: "bg-indigo-50 text-indigo-700",
  SECURITY: "bg-red-50 text-red-700",
  REFERENCE_DATA: "bg-slate-100 text-slate-600",
  WORKFLOW: "bg-violet-50 text-violet-700",
  SYSTEM: "bg-slate-200 text-slate-700",
  MEMBERSHIP: "bg-sky-50 text-sky-700",
  PROCUREMENT: "bg-orange-50 text-orange-700",
  TRAINING: "bg-purple-50 text-purple-700",
  CLAIM: "bg-amber-50 text-amber-700",
  INVENTORY: "bg-teal-50 text-teal-700",
  REQUISITION: "bg-orange-50 text-orange-700",
  CALENDAR: "bg-pink-50 text-pink-700",
  DOCUMENT: "bg-stone-50 text-stone-700",
};

const ACTION_BADGE: Record<string, string> = {
  CREATED: "bg-emerald-50 text-emerald-700",
  UPDATED: "bg-blue-50 text-blue-700",
  DELETED: "bg-red-50 text-red-700",
  ACTIVATED: "bg-emerald-50 text-emerald-700",
  DEACTIVATED: "bg-slate-100 text-slate-600",
  SUBMITTED: "bg-blue-50 text-blue-700",
  APPROVED: "bg-emerald-50 text-emerald-700",
  REJECTED: "bg-red-50 text-red-700",
  CANCELLED: "bg-slate-100 text-slate-600",
  LOGIN_SUCCESS: "bg-emerald-50 text-emerald-700",
  LOGIN_FAILED: "bg-red-50 text-red-700",
  PASSWORD_RESET_REQUESTED: "bg-amber-50 text-amber-700",
  PASSWORD_CHANGED: "bg-amber-50 text-amber-700",
  ROLE_ASSIGNED: "bg-violet-50 text-violet-700",
  ROLE_REMOVED: "bg-violet-50 text-violet-700",
  PERMISSION_ASSIGNED: "bg-violet-50 text-violet-700",
  PERMISSION_REMOVED: "bg-violet-50 text-violet-700",
  EXPORTED: "bg-slate-100 text-slate-600",
  IMPORTED: "bg-slate-100 text-slate-600",
  VIEWED: "bg-slate-100 text-slate-500",
  PAID: "bg-emerald-50 text-emerald-700",
  DISBURSED: "bg-emerald-50 text-emerald-700",
  SYSTEM_EVENT: "bg-slate-200 text-slate-700",
};

function fullTimestamp(value?: string | null): string {
  if (!value) return "-";
  return new Date(value).toLocaleString("en-KE", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
}

function timeAgo(value?: string | null): string {
  if (!value) return "";
  const diff = Date.now() - new Date(value).getTime();
  const m = Math.floor(diff / 60000);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  if (h < 24) return `${h}h ago`;
  if (d < 7) return `${d}d ago`;
  return new Date(value).toLocaleDateString("en-KE", { day: "numeric", month: "short" });
}

const EMPTY_FILTERS: AuditSearchFilters = {};

/** Turns the applied filters into a short, readable line for a report's subtitle. */
function describeFilters(filters: AuditSearchFilters): string {
  const parts: string[] = [];
  if (filters.module) parts.push(`Module: ${filters.module.replaceAll("_", " ")}`);
  if (filters.action) parts.push(`Action: ${filters.action.replaceAll("_", " ")}`);
  if (filters.entityType) parts.push(`Entity: ${filters.entityType}`);
  if (filters.actorUserId) parts.push(`Actor user #${filters.actorUserId}`);
  if (filters.from) parts.push(`From ${new Date(filters.from).toLocaleString("en-KE")}`);
  if (filters.to) parts.push(`To ${new Date(filters.to).toLocaleString("en-KE")}`);
  return parts.length > 0 ? parts.join("  ·  ") : "All events, no filters applied";
}

export default function AuditLogPage() {
  const toast = useToast();
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<AuditSearchFilters>(EMPTY_FILTERS);
  const [selected, setSelected] = useState<AuditEvent | null>(null);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);

  // ── Entity type: free-typed, autocompletes against every entityType value ever recorded ──
  const [entityTypeInput, setEntityTypeInput] = useState("");
  const [entityTypeOpen, setEntityTypeOpen] = useState(false);
  const debouncedEntityType = useDebouncedValue(entityTypeInput, 400);
  const entityTypesQuery = useQuery({
    queryKey: ["audit-entity-types"],
    queryFn: auditApi.entityTypes,
    staleTime: 5 * 60 * 1000,
  });
  const entityTypeSuggestions = (entityTypesQuery.data ?? []).filter((t) =>
    t.toLowerCase().includes(entityTypeInput.trim().toLowerCase()),
  ).slice(0, 12);

  // ── Actor: real-time search over authenticated system users (name or username), with a
  // browsable dropdown of users shown as soon as the field is focused — even before typing ──
  const [actorQuery, setActorQuery] = useState("");
  const [actorLabel, setActorLabel] = useState("");
  const [actorOpen, setActorOpen] = useState(false);
  const debouncedActorQuery = useDebouncedValue(actorQuery, 200);
  const actorSearchQuery = useQuery({
    queryKey: ["audit-actor-search", debouncedActorQuery],
    queryFn: () => auditApi.searchActors(debouncedActorQuery),
    enabled: actorOpen && !actorLabel,
    staleTime: 60 * 1000,
  });

  // ── From / To: debounced so mid-edit keystrokes don't each trigger a search ──
  const [fromInput, setFromInput] = useState("");
  const [toInput, setToInput] = useState("");
  const debouncedFrom = useDebouncedValue(fromInput, 500);
  const debouncedTo = useDebouncedValue(toInput, 500);

  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    setFilters((f) => ({ ...f, entityType: debouncedEntityType.trim() || undefined }));
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEntityType]);

  const fromToFirstRun = useRef(true);
  useEffect(() => {
    if (fromToFirstRun.current) { fromToFirstRun.current = false; return; }
    setFilters((f) => ({ ...f, from: localDateTimeInputToIso(debouncedFrom), to: localDateTimeInputToIso(debouncedTo) }));
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFrom, debouncedTo]);

  const query = useAuditEvents(filters, page, PAGE_SIZE);

  function eventRow(e: AuditEvent, i: number): (string | number)[] {
    return [
      i + 1,
      new Date(e.eventAt).toLocaleString("en-KE", {
        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      }),
      e.module.replaceAll("_", " "),
      e.action.replaceAll("_", " "),
      e.summary || e.entityReference || `${e.entityType ?? ""} #${e.entityId ?? ""}`,
      e.actorUsername || (e.actorUserId ? `User #${e.actorUserId}` : "System"),
    ];
  }

  async function handleExportExcel() {
    setExportingExcel(true);
    try {
      const { events, truncated } = await auditApi.searchAll(filters);
      await exportToExcel(
        "Audit Log Report",
        ["#", "Time", "Module", "Action", "Summary", "Actor"],
        events.map((e, i) => eventRow(e, i)),
        "audit-log-report",
      );
      toast.success(
        truncated
          ? `Exported the first ${events.length.toLocaleString()} events — narrow the filters to capture the rest.`
          : `Exported ${events.length.toLocaleString()} event${events.length === 1 ? "" : "s"}.`,
      );
    } catch {
      toast.error("Could not generate the Excel report. Please try again.");
    } finally {
      setExportingExcel(false);
    }
  }

  async function handleExportPdf() {
    setExportingPdf(true);
    try {
      const { events, truncated } = await auditApi.searchAll(filters);
      await exportToPdf({
        title: "Audit Log Report",
        subtitle: `${describeFilters(filters)}  ·  ${events.length.toLocaleString()} events`,
        headers: ["#", "Time", "Module", "Action", "Summary", "Actor"],
        rows: events.map((e, i) => eventRow(e, i)),
        filename: "audit-log-report",
      });
      toast.success(
        truncated
          ? `Exported the first ${events.length.toLocaleString()} events — narrow the filters to capture the rest.`
          : `Exported ${events.length.toLocaleString()} event${events.length === 1 ? "" : "s"}.`,
      );
    } catch {
      toast.error("Could not generate the PDF report. Please try again.");
    } finally {
      setExportingPdf(false);
    }
  }

  function clearFilters() {
    setFilters(EMPTY_FILTERS);
    setEntityTypeInput("");
    setActorQuery("");
    setActorLabel("");
    setFromInput("");
    setToInput("");
    setPage(0);
  }

  const items = query.data?.content ?? [];
  const totalPages = query.data?.totalPages ?? 0;
  const totalElems = query.data?.totalElements ?? 0;
  const currentPage = query.data?.number ?? 0;
  const offset = currentPage * PAGE_SIZE;

  if (query.isLoading) return <PageLoader label="Loading audit log…" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Failed to load audit log"
        message={query.error instanceof Error ? query.error.message : "Could not load audit events."}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h1 className="flex items-center gap-2 text-xl font-bold text-slate-900">
          <History className="h-5 w-5 text-slate-400" />
          Audit Log
        </h1>
      </div>

      {/* ── Filters ── */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Module</label>
            <Select
              value={filters.module ?? ""}
              onChange={(e) => {
                setFilters((f) => ({ ...f, module: e.target.value as AuditSearchFilters["module"] }));
                setPage(0);
              }}
            >
              <option value="">All modules</option>
              {AUDIT_MODULES.map((m) => (
                <option key={m} value={m}>{m.replaceAll("_", " ")}</option>
              ))}
            </Select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Action</label>
            <Select
              value={filters.action ?? ""}
              onChange={(e) => {
                setFilters((f) => ({ ...f, action: e.target.value as AuditSearchFilters["action"] }));
                setPage(0);
              }}
            >
              <option value="">All actions</option>
              {AUDIT_ACTIONS.map((a) => (
                <option key={a} value={a}>{a.replaceAll("_", " ")}</option>
              ))}
            </Select>
          </div>

          <div className="relative">
            <label className="mb-1 block text-xs font-semibold text-slate-500">Entity type</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-300" />
              <input
                type="text"
                placeholder="Search entity types…"
                value={entityTypeInput}
                onChange={(e) => setEntityTypeInput(e.target.value)}
                onFocus={() => setEntityTypeOpen(true)}
                onBlur={() => setTimeout(() => setEntityTypeOpen(false), 150)}
                className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-8 pr-7 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              />
              {entityTypeInput && (
                <button
                  type="button"
                  onMouseDown={() => setEntityTypeInput("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {entityTypeOpen && entityTypeSuggestions.length > 0 && (
              <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                {entityTypeSuggestions.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onMouseDown={() => { setEntityTypeInput(t); setEntityTypeOpen(false); }}
                    className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-emerald-50"
                  >
                    {t}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <label className="mb-1 block text-xs font-semibold text-slate-500">Actor</label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-300" />
              <input
                type="text"
                placeholder="Search by name or username…"
                value={actorLabel || actorQuery}
                onChange={(e) => {
                  setActorLabel("");
                  setActorQuery(e.target.value);
                  setActorOpen(true);
                  if (filters.actorUserId) {
                    setFilters((f) => ({ ...f, actorUserId: undefined }));
                    setPage(0);
                  }
                }}
                onFocus={() => setActorOpen(true)}
                onBlur={() => setTimeout(() => setActorOpen(false), 150)}
                className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-8 pr-7 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
              />
              {(actorLabel || actorQuery) && (
                <button
                  type="button"
                  onMouseDown={() => {
                    setActorLabel("");
                    setActorQuery("");
                    if (filters.actorUserId) {
                      setFilters((f) => ({ ...f, actorUserId: undefined }));
                      setPage(0);
                    }
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-300 hover:text-slate-500"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
            {actorOpen && !actorLabel && (
              <div className="absolute z-20 mt-1 max-h-52 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg">
                {actorSearchQuery.isFetching ? (
                  <p className="px-3 py-2 text-xs text-slate-400">Searching…</p>
                ) : (actorSearchQuery.data ?? []).length === 0 ? (
                  <p className="px-3 py-2 text-xs text-slate-400">
                    {actorQuery.trim() ? "No matching users" : "No authenticated users found"}
                  </p>
                ) : (
                  <>
                    {!actorQuery.trim() && (
                      <p className="px-3 pb-1 pt-0.5 text-[11px] font-medium uppercase tracking-wide text-slate-400">
                        System users
                      </p>
                    )}
                    {(actorSearchQuery.data ?? []).map((u: AuditActorOption) => (
                    <button
                      key={u.id}
                      type="button"
                      onMouseDown={() => {
                        setActorLabel(u.name);
                        setActorQuery("");
                        setActorOpen(false);
                        setFilters((f) => ({ ...f, actorUserId: u.id }));
                        setPage(0);
                      }}
                      className="block w-full px-3 py-1.5 text-left text-sm hover:bg-emerald-50"
                    >
                      <span className="font-medium text-slate-700">{u.name}</span>
                      <span className="ml-1.5 text-xs text-slate-400">@{u.username}</span>
                    </button>
                    ))}
                  </>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">From</label>
            <input
              type="datetime-local"
              value={fromInput}
              onChange={(e) => setFromInput(e.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">To</label>
            <input
              type="datetime-local"
              value={toInput}
              onChange={(e) => setToInput(e.target.value)}
              className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100"
            />
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={clearFilters}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-sm hover:bg-slate-50"
          >
            <RotateCcw className="h-3.5 w-3.5" />
            Clear
          </button>

          <div className="ml-auto flex items-center gap-2">
            <span className="hidden items-center gap-1.5 text-xs font-medium text-slate-400 sm:flex">
              <Download className="h-3.5 w-3.5" />
              Generate report
            </span>
            <button
              type="button"
              onClick={handleExportExcel}
              disabled={exportingExcel}
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              <FileSpreadsheet className="h-3.5 w-3.5" />
              {exportingExcel ? "Exporting…" : "Excel"}
            </button>
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exportingPdf}
              className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-600 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              <FileText className="h-3.5 w-3.5" />
              {exportingPdf ? "Exporting…" : "PDF"}
            </button>
          </div>
        </div>
      </div>

      {/* ── Table ── */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="grid grid-cols-[2.5rem_9rem_7rem_7rem_1fr_10rem_3rem] border-b border-slate-100 bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          <span>#</span>
          <span>Time</span>
          <span>Module</span>
          <span>Action</span>
          <span>Summary</span>
          <span>Actor</span>
          <span />
        </div>

        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100">
              <History className="h-5 w-5 text-slate-300" />
            </div>
            <p className="text-sm font-semibold text-slate-600">No audit events</p>
            <p className="text-xs text-slate-400">Nothing matches the current filters.</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-100">
            {items.map((item, idx) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelected(item)}
                className="grid w-full grid-cols-[2.5rem_9rem_7rem_7rem_1fr_10rem_3rem] items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-slate-50"
              >
                <span className="text-xs text-slate-400 pl-1">{offset + idx + 1}</span>

                <span className="text-[11px] text-slate-400 tabular-nums" title={fullTimestamp(item.eventAt)}>
                  {timeAgo(item.eventAt)}
                </span>

                <span>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${MODULE_BADGE[item.module] ?? "bg-slate-100 text-slate-600"}`}>
                    {item.module.replaceAll("_", " ")}
                  </span>
                </span>

                <span>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${ACTION_BADGE[item.action] ?? "bg-slate-100 text-slate-600"}`}>
                    {item.action.replaceAll("_", " ")}
                  </span>
                </span>

                <span className="min-w-0 truncate text-sm text-slate-700">
                  {item.summary || item.entityReference || `${item.entityType ?? ""} #${item.entityId ?? ""}`}
                </span>

                <span className="truncate text-xs text-slate-500">
                  {item.actorUsername || (item.actorUserId ? `User #${item.actorUserId}` : "System")}
                </span>

                <span className="flex justify-center">
                  <Eye className="h-3.5 w-3.5 text-slate-300" />
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-400">
            Page {currentPage + 1} of {totalPages} · {totalElems} events
          </p>

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={currentPage === 0}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm hover:bg-slate-50 disabled:opacity-30 disabled:cursor-default transition"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>

            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              const start = Math.max(0, Math.min(currentPage - 3, totalPages - 7));
              const p = totalPages > 7 ? start + i : i;
              return (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  className={`h-7 w-7 rounded-lg text-xs font-medium transition ${
                    p === currentPage
                      ? "bg-emerald-600 text-white shadow-sm"
                      : "border border-slate-200 bg-white text-slate-500 hover:bg-slate-50"
                  }`}
                >
                  {p + 1}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={currentPage >= totalPages - 1}
              className="flex h-7 w-7 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm hover:bg-slate-50 disabled:opacity-30 disabled:cursor-default transition"
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* ── Detail modal ── */}
      <Modal open={selected !== null} title="Audit event" onClose={() => setSelected(null)}>
        {selected && (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${MODULE_BADGE[selected.module] ?? "bg-slate-100 text-slate-600"}`}>
                {selected.module.replaceAll("_", " ")}
              </span>
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${ACTION_BADGE[selected.action] ?? "bg-slate-100 text-slate-600"}`}>
                {selected.action.replaceAll("_", " ")}
              </span>
              <span className="text-xs text-slate-400">{fullTimestamp(selected.eventAt)}</span>
            </div>

            <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">
              {selected.summary || "No summary was recorded for this event."}
            </p>

            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-2xl bg-slate-50 p-4 text-xs">
              <dt className="font-semibold text-slate-500">Actor</dt>
              <dd className="text-slate-700">{selected.actorUsername || (selected.actorUserId ? `User #${selected.actorUserId}` : "System")}</dd>

              <dt className="font-semibold text-slate-500">Entity</dt>
              <dd className="text-slate-700">
                {selected.entityReference || (selected.entityType ? `${selected.entityType} #${selected.entityId ?? "-"}` : "-")}
              </dd>

              <dt className="font-semibold text-slate-500">IP address</dt>
              <dd className="text-slate-700">{selected.ipAddress || "-"}</dd>

              <dt className="font-semibold text-slate-500">Request ID</dt>
              <dd className="truncate text-slate-700">{selected.requestId || "-"}</dd>

              <dt className="font-semibold text-slate-500">User agent</dt>
              <dd className="col-span-1 truncate text-slate-700" title={selected.userAgent ?? undefined}>{selected.userAgent || "-"}</dd>
            </dl>

            {selected.details && (() => {
              const info = describeAuditDetails(selected.details);
              return (
                <div>
                  <p className="mb-1 text-xs font-semibold text-slate-500">What happened</p>
                  <div className="space-y-2 rounded-2xl bg-slate-50 p-4">
                    {info.statusLabel && (
                      <span
                        className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
                          info.succeeded
                            ? "bg-emerald-100 text-emerald-700"
                            : info.failed
                              ? "bg-red-100 text-red-700"
                              : "bg-slate-200 text-slate-700"
                        }`}
                      >
                        {info.succeeded ? (
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        ) : info.failed ? (
                          <XCircle className="h-3.5 w-3.5" />
                        ) : null}
                        {info.statusLabel}
                      </span>
                    )}
                    <p className="text-sm leading-relaxed text-slate-700">
                      {info.message || "No additional details were recorded for this event."}
                    </p>
                  </div>
                  <details key={selected.id} className="mt-2 text-xs text-slate-400">
                    <summary className="cursor-pointer select-none hover:text-slate-600">
                      Show technical details
                    </summary>
                    <pre className="mt-2 max-h-56 overflow-auto rounded-2xl bg-slate-900 p-4 text-[11px] leading-relaxed text-slate-100">
                      {info.raw}
                    </pre>
                  </details>
                </div>
              );
            })()}
          </div>
        )}
      </Modal>
    </div>
  );
}
